import React, { useState } from 'react';
import { Heart, AlertCircle, Eye, Trophy, Star } from 'lucide-react';
import { getLatestEvaluationForHorse, getAverageScoresForHorse } from '../data/evaluationData';

const HorseThumbnail = ({ horseId, horseName }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-12 h-12 rounded-md bg-gray-100 flex-shrink-0 flex items-center justify-center">
        <span className="text-gray-400 text-xs font-semibold">
          {horseName ? horseName.charAt(0).toUpperCase() : '?'}
        </span>
      </div>
    );
  }

  return (
    <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
      <img
        src={`https://horseapp.blob.core.windows.net/horses/${horseId}.jpg`}
        alt={horseName}
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const HorseTable = ({ horses, onSelectHorse, findHorseById }) => {
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Lifandi': return <Heart className="w-4 h-4 text-green-500" />;
      case 'Fallin': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Lifandi': return 'bg-green-100 text-green-800';
      case 'Fallin': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 9.0) return 'bg-purple-100 text-purple-800';
    if (rating >= 8.5) return 'bg-blue-100 text-blue-800';
    if (rating >= 8.0) return 'bg-green-100 text-green-800';
    if (rating >= 7.5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const renderParentLink = (parentName, parentId) => {
    if (!parentName) return 'Óþekktur';

    const parentHorse = findHorseById(parentId);
    if (parentHorse) {
      return (
        <button
          onClick={() => onSelectHorse(parentHorse)}
          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
        >
          {parentName}
        </button>
      );
    }
    return parentName;
  };

  if (horses.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="text-center py-12">
          <div className="w-12 h-12 text-gray-300 mx-auto mb-4">🔍</div>
          <p className="text-gray-500">Engin hross fundust með þessari leit</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hross</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Foreldrar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kynbótamat</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dómar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staða</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Land</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nánar</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {horses.map((horse) => {
              const latestEvaluation = getLatestEvaluationForHorse(horse.id);
              const averageScores = getAverageScoresForHorse(horse.id);

              return (
                <tr key={horse.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <HorseThumbnail horseId={horse.id} horseName={horse.name} />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{horse.name}</div>
                        <div className="text-sm text-gray-500">{horse.id}</div>
                        <div className="text-xs text-gray-400">Fætt {horse.year}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div>
                        <span className="font-medium">Faðir:</span> {renderParentLink(horse.father, horse.fatherId)}
                      </div>
                      <div>
                        <span className="font-medium">Móðir:</span> {renderParentLink(horse.mother, horse.motherId)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {horse.rating > 0 && (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRatingColor(horse.rating)}`}>
                        {horse.rating}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {latestEvaluation && latestEvaluation.totalScore && (
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-blue-500" />
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getRatingColor(latestEvaluation.totalScore)}`}>
                            {latestEvaluation.totalScore}
                          </span>
                          <span className="text-xs text-gray-500">({latestEvaluation.year})</span>
                        </div>
                      )}
                      {averageScores && (
                        <div className="flex items-center space-x-1">
                          <Trophy className="w-3 h-3 text-green-500" />
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getRatingColor(parseFloat(averageScores.totalScore))}`}>
                            ⌀{averageScores.totalScore}
                          </span>
                          <span className="text-xs text-gray-500">({averageScores.evaluationCount} dómar)</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {getStatusIcon(horse.status)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(horse.status)}`}>
                        {horse.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{horse.country}</span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => onSelectHorse(horse)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HorseTable;
